import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import style from './BottomSheetBase.module.css';

const BottomSheetBase = ({ open, onDismiss, children, footer, maxHeight = '85vh' }) => {
    const [visible, setVisible] = useState(false);
    const [active, setActive] = useState(false);
    const sheetRef = useRef(null);
    const contentRef = useRef(null);
    const startY = useRef(0);
    const currentY = useRef(0);
    const isDragging = useRef(false);

    useEffect(() => {
        if (open) {
            setVisible(true);
            requestAnimationFrame(() => {
                requestAnimationFrame(() => setActive(true));
            });

            // 배경 스크롤 차단
            const scrollY = window.scrollY;
            document.body.style.position = 'fixed';
            document.body.style.top = `-${scrollY}px`;
            document.body.style.left = '0';
            document.body.style.right = '0';
            document.body.style.overflow = 'hidden';

            return () => {
                document.body.style.position = '';
                document.body.style.top = '';
                document.body.style.left = '';
                document.body.style.right = '';
                document.body.style.overflow = '';
                window.scrollTo(0, scrollY);
            };
        } else {
            setActive(false);
            const timer = setTimeout(() => setVisible(false), 300);
            return () => clearTimeout(timer);
        }
    }, [open]);

    const handleDismiss = useCallback(() => {
        onDismiss?.();
    }, [onDismiss]);

    const handleTouchStart = (e) => {
        startY.current = e.touches[0].clientY;
        currentY.current = e.touches[0].clientY;
        isDragging.current = false;
    };

    const handleTouchMove = (e) => {
        currentY.current = e.touches[0].clientY;
        const diff = currentY.current - startY.current;

        const target = e.target;
        const isInsideScrollable = target.closest('.picker-scroll-container') || target.closest('.picker-column');
        if (isInsideScrollable) {
            isDragging.current = false;
            return;
        }

        const scrollTop = contentRef.current?.scrollTop ?? 0;

        if (diff > 0 && scrollTop <= 0) {
            isDragging.current = true;
            if (sheetRef.current) {
                sheetRef.current.style.transform = `translateY(${diff}px)`;
                sheetRef.current.style.transition = 'none';
            }
            e.preventDefault();
        } else {
            isDragging.current = false;
        }
    };

    const handleTouchEnd = () => {
        if (sheetRef.current) {
            sheetRef.current.style.transition = '';
            sheetRef.current.style.transform = '';
        }

        if (isDragging.current) {
            const diff = currentY.current - startY.current;
            if (diff > 100) {
                handleDismiss();
            }
        }

        startY.current = 0;
        currentY.current = 0;
        isDragging.current = false;
    };

    if (!visible) return null;

    return createPortal(
        <div className={`${style.overlay} ${active ? style.overlayActive : ''}`} onClick={handleDismiss}>
            <div
                ref={sheetRef}
                className={`${style.sheet} ${active ? style.sheetActive : ''}`}
                style={{ maxHeight }}
                onClick={(e) => e.stopPropagation()}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
            >
                <div className={style.handle}>
                    <span className={style.handleBar} />
                    <button className={style.closeBtn} onClick={handleDismiss}>
                        <span className="blind">닫기</span>
                    </button>
                </div>
                <div ref={contentRef} className={style.content}>
                    {children}
                </div>
                {footer && (
                    <div className={style.footer}>
                        {footer}
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
};

export default BottomSheetBase;